import React, { useEffect, useState } from 'react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { ArrowUpRight, Clock, Landmark, Loader2, TrendingUp } from 'lucide-react';
import { sellerApi } from '../sellerAuth';
import { money } from './helpers';

const StatCard = ({ label, value, sub, full, testid }) => (
  <div data-testid={testid} className={`rounded-3xl bg-card border border-border p-4 ${full ? 'col-span-2' : ''}`}>
    <p className="font-heading text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{label}</p>
    <p className="font-heading font-black tracking-tight mt-1.5" style={{ fontSize: full ? '2.25rem' : '1.6rem' }}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const DashboardScreen = ({ seller }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sellerApi.get('/seller/dashboard').then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="animate-spin text-primary" size={36} /></div>;
  if (!data) return <div className="px-5 py-20 text-center text-muted-foreground">Could not load dashboard.</div>;

  const feesTotal = data.total_stripe_fees + data.total_platform_fees + data.total_net;
  const seg = (v) => (feesTotal > 0 ? (v / feesTotal) * 100 : 0);

  return (
    <div className="px-5 pt-6">
      <p className="font-heading text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Welcome back</p>
      <h1 className="font-heading text-3xl font-black tracking-tight truncate">{seller?.name || 'Merchant'}</h1>

      {/* Hero balance */}
      <div className="mt-4 rounded-3xl p-5 bg-gradient-to-br from-primary to-[#0a3d2b] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.15), transparent 45%)' }} />
        <div className="relative">
          <p className="text-sm text-white/70">Available to pay out</p>
          <p data-testid="dashboard-payout" className="font-heading text-4xl font-black tracking-tight mt-1">{money(data.available_payout)}</p>
          <div className="flex items-center gap-2 mt-3 text-sm text-white/80">
            <Clock size={15} strokeWidth={2.5} /> {money(data.pending_payout)} pending · Balance {money(data.balance)}
          </div>
        </div>
      </div>

      {/* Sales cards */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <StatCard testid="stat-today" full label="Today's sales" value={money(data.today.net)} sub={`${data.today.count} transactions`} />
        <StatCard testid="stat-week" label="This week" value={money(data.week.net)} sub={`${data.week.count} sales`} />
        <StatCard testid="stat-month" label="This month" value={money(data.month.net)} sub={`${data.month.count} sales`} />
      </div>

      {/* Trend */}
      <div className="mt-3 rounded-3xl bg-card border border-border p-4">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={16} strokeWidth={2.5} className="text-primary" />
          <p className="font-heading text-sm font-bold">Last 7 days</p>
        </div>
        <div className="h-28">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100}>
            <AreaChart data={data.trend} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
              <Tooltip cursor={{ stroke: 'hsl(var(--border))' }} contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 12, fontSize: 12, color: 'hsl(var(--foreground))' }} formatter={(v) => [money(v), 'Sales']} />
              <Area type="monotone" dataKey="total" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Fees breakdown */}
      <div className="mt-3 rounded-3xl bg-card border border-border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Landmark size={16} strokeWidth={2.5} className="text-primary" />
          <p className="font-heading text-sm font-bold">Where your money went (all time)</p>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-secondary">
          <div style={{ width: `${seg(data.total_net)}%` }} className="bg-primary" />
          <div style={{ width: `${seg(data.total_stripe_fees)}%` }} className="bg-orange-400" />
          <div style={{ width: `${seg(data.total_platform_fees)}%` }} className="bg-blue-400" />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <Legend color="bg-primary" label="Net payout" value={money(data.total_net)} />
          <Legend color="bg-orange-400" label="Stripe fees" value={money(data.total_stripe_fees)} />
          <Legend color="bg-blue-400" label="Platform fees" value={money(data.total_platform_fees)} />
        </div>
      </div>

      {/* Recent */}
      <div className="mt-3 mb-2">
        <p className="font-heading text-sm font-bold mb-2">Recent activity</p>
        {(!data.recent || data.recent.length === 0) ? (
          <p className="text-sm text-muted-foreground py-6 text-center rounded-3xl bg-card border border-border">No sales yet.</p>
        ) : (
          <div className="rounded-3xl bg-card border border-border divide-y divide-border">
            {data.recent.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-primary"><ArrowUpRight size={16} strokeWidth={2.75} /></div>
                  <div>
                    <p className="text-sm font-bold capitalize">{t.method} sale</p>
                    <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className={`font-heading font-bold ${t.status?.includes('refund') ? 'text-muted-foreground line-through' : 'text-primary'}`}>{money(t.net)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Legend = ({ color, label, value }) => (
  <div>
    <div className="flex items-center justify-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${color}`} /><span className="text-[11px] text-muted-foreground">{label}</span></div>
    <p className="text-sm font-bold mt-0.5">{value}</p>
  </div>
);

export default DashboardScreen;
